import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

/**
 * Fullscreen image lightbox — reusable, dependency-free.
 * Props:
 *   images: string[]                (image URLs)
 *   index:  number                  (current index)
 *   onIndexChange: (i) => void
 *   onClose: () => void
 * Features: prev/next, keyboard (←/→/Esc), click-to-zoom with panning,
 * mobile swipe, counter. Mobile friendly.
 */
export default function ImageLightbox({ images = [], index = 0, onIndexChange, onClose }) {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState('center center');
  const touchStart = useRef(null);

  const count = images.length;
  const go = useCallback((dir) => {
    setZoom(false);
    onIndexChange(((index + dir) % count + count) % count);
  }, [index, count, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [go, onClose]);

  if (!count) return null;

  const onMove = (e) => {
    if (!zoom) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 z-20 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
        aria-label="Fermer"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Counter + zoom hint */}
      <div className="absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white">
        {index + 1} / {count}
        <span className="text-white/60">·</span>
        {zoom ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
      </div>

      {/* Prev / Next */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-2 z-20 rounded-full bg-white/10 p-2.5 text-white transition hover:bg-white/20 sm:left-6"
            aria-label="Précédent"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-2 z-20 rounded-full bg-white/10 p-2.5 text-white transition hover:bg-white/20 sm:right-6"
            aria-label="Suivant"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Image */}
      <div
        className="flex h-full w-full items-center justify-center p-6 sm:p-16"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={onMove}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={images[index]}
          alt=""
          onClick={() => setZoom((z) => !z)}
          style={{ transformOrigin: origin }}
          className={`max-h-full max-w-full select-none rounded-lg object-contain shadow-2xl transition-transform duration-200 ${
            zoom ? 'scale-[2.2] cursor-zoom-out' : 'cursor-zoom-in'
          }`}
          draggable={false}
        />
      </div>

      {/* Thumbnails */}
      {count > 1 && (
        <div
          className="absolute bottom-4 left-1/2 z-20 flex max-w-[92vw] -translate-x-1/2 gap-2 overflow-x-auto rounded-xl bg-white/10 p-2 backdrop-blur"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setZoom(false); onIndexChange(i); }}
              className={`h-12 w-16 shrink-0 overflow-hidden rounded-md border-2 transition ${
                i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
